import React, { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Check, Close, DriveFileMove } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';

import { IKiAblageProposal } from '../Interfaces/IKi';
import { customFetcher } from '../hooks/useFetch';

interface IProps {
  proposal: IKiAblageProposal;
}

/**
 * Vorschlagskarte: eine Datei aus dem Chat im Vorgang ablegen (#1336).
 *
 * Die KI hat das Ziel **bestimmt und geprüft**, abgelegt ist noch nichts.
 * Der Klick schickt nur die Datei an `POST /ki/ablage/{id}`; der Server lädt
 * den Vorgang, hängt sie ein und speichert über denselben Vorgangs-Save wie
 * der Editor — mit der Sitzung des Benutzers, und mit allem, was daran hängt:
 * Mandanten-Benachrichtigung, Portal, ArnoTop, Protokoll.
 *
 * Was davon die Mandanten betrifft, steht **vor** dem Klick auf der Karte
 * (`Folgen`). Wer im Chat ablegt, sieht den Editor nicht und weiß sonst nicht,
 * dass eine Urkunde eine E-Mail an die Beteiligten auslöst.
 */
export const KiAblageProposalCard: React.FC<IProps> = ({ proposal }) => {
  const queryClient = useQueryClient();

  const [state, setState] = useState<'open' | 'done' | 'dismissed'>('open');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state === 'dismissed') {
    return null;
  }

  const { Display, Payload, Datei } = proposal;
  const folgen = Display.Folgen ?? [];

  if (state === 'done') {
    return (
      <Alert severity='success' icon={<DriveFileMove fontSize='inherit' />}>
        „{Display.Datei}" liegt jetzt in {Display.Vorgang} ({ziel(proposal)}).
      </Alert>
    );
  }

  async function handleAblage() {
    setError(null);

    if (Datei === undefined) {
      setError(
        'Die Datei hängt nicht mehr am Gespräch. Bitte erneut anhängen und fragen.'
      );
      return;
    }

    setIsLoading(true);

    try {
      await customFetcher({
        endpoint: `/ki/ablage/${Payload.idLegalTransaction}`,
        method: 'POST',
        body: { ...Payload, Attachment: Datei },
      });

      await queryClient.invalidateQueries({
        queryKey: ['legaltransaction/fullObject', Payload.idLegalTransaction],
      });

      setState('done');
    } catch (exception) {
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(
        detail !== '' ? detail : 'Das Dokument konnte nicht abgelegt werden.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderColor: 'primary.light' }}>
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <DriveFileMove fontSize='small' color='primary' />
          <Typography variant='subtitle2'>Dokument ablegen</Typography>
        </Stack>

        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
          <Chip size='small' label={Display.Datei ?? 'Datei'} />
          <Chip
            size='small'
            color='primary'
            variant='outlined'
            label={`Vorgang: ${Display.Vorgang ?? Payload.idLegalTransaction}${
              Display.Aktenzeichen ? ` (${Display.Aktenzeichen})` : ''
            }`}
          />
          <Chip size='small' variant='outlined' label={ziel(proposal)} />
        </Stack>

        {folgen.length > 0 && (
          // Warnung, wenn Mandanten etwas sehen oder bekommen — das ist der
          // Teil, den man vor dem Klick wissen muss.
          <Alert
            severity={Display.BetrifftMandanten ? 'warning' : 'info'}
            sx={{ py: 0 }}
          >
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {folgen.map(folge => (
                <Typography component='li' variant='caption' key={folge}>
                  {folge}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {proposal.Hinweise.length > 0 && (
          <Alert severity='info' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {proposal.Hinweise.map(hinweis => (
                <Typography component='li' variant='caption' key={hinweis}>
                  {hinweis}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {Datei === undefined && (
          <Alert severity='warning'>
            Die Datei hängt nicht mehr am Gespräch — so lässt sie sich nicht
            ablegen.
          </Alert>
        )}

        {error !== null && <Alert severity='error'>{error}</Alert>}

        <Stack
          direction='row'
          spacing={1}
          justifyContent='flex-end'
          alignItems='center'
        >
          {isLoading && <CircularProgress size={16} />}
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            disabled={isLoading}
            onClick={() => setState('dismissed')}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={<Check />}
            disabled={isLoading || Datei === undefined}
            onClick={() => void handleAblage()}
          >
            Ablegen
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

/** „Maßnahme: X (Phase Y)", „Urkunden", „Interne Datenablage: Ordner". */
const ziel = ({ Display }: IKiAblageProposal): string => {
  if (Display.Massnahme) {
    return `Maßnahme: ${Display.Massnahme}${Display.Phase ? ` (${Display.Phase})` : ''}`;
  }

  if (Display.Ordner) {
    return `${Display.Ziel}: ${Display.Ordner}`;
  }

  return Display.Ziel;
};
